import { ReactNode } from 'react';
import { Avatar, TreeDataNode } from 'antd';
import {
    TbBuilding,
    TbFileText,
    TbSearch,
    TbSettings,
    TbUser,
    TbUsersGroup,
} from 'react-icons/tb';

import { WorkspaceResourceAvatar, WorkspaceResourceGlyph } from '@/shared/components/workspace-resources';

export type OAuthScopeCategory = 'account' | 'workspace' | 'resources';

export interface OAuthScopeDefinition {
    key: string;
    label: string;
    description: string;
    category: OAuthScopeCategory;
}

export interface OAuthConsentClient {
    id?: string;
    iconUrl?: string;
    name?: string;
    shortDescription?: string;
    overview?: string;
    isPublic?: boolean;
    slug?: string;
    developerName?: string;
    developerVerified?: boolean;
    supportEmail?: string;
    supportURL?: string;
    supportUrl?: string;
    websiteUrl?: string;
    installationUrl?: string;
}

export interface OAuthConsentRequest {
    client?: OAuthConsentClient;
    requestedScopes?: string[];
}

interface ScopePresentation {
    label: string;
    description: string;
    icon: ReactNode;
}

const resourceDefinitions = [
    ['folder', 'Folders'],
    ['investment', 'Investments'],
    ['memo', 'Memos'],
    ['pipeline', 'Pipelines'],
    ['team', 'Teams'],
    ['dashboard', 'Dashboards'],
    ['list', 'Lists'],
    ['report', 'Reports'],
    ['task', 'Tasks'],
] as const;

const resourceActions = [
    ['read', 'Read', 'View resources visible to the authorizing user.'],
    ['create', 'Create', 'Create new resources in the authorized workspace.'],
    ['update', 'Update', 'Edit resources visible to the authorizing user.'],
    ['manage', 'Manage', 'Archive, restore, or delete authorized resources.'],
] as const;

export const defaultOAuthScopeCatalog: OAuthScopeDefinition[] = [
    {
        key: 'user.read',
        label: 'View user profile',
        description: 'View the authorizing user and their active workspace context.',
        category: 'account',
    },
    {
        key: 'user.update',
        label: 'Update user profile',
        description: 'Update the authorizing user profile.',
        category: 'account',
    },
    {
        key: 'member.read',
        label: 'View workspace membership',
        description: "View the authorizing user's active workspace membership.",
        category: 'account',
    },
    {
        key: 'workspace.read',
        label: 'View workspace',
        description: 'View workspace details available to the authorizing user.',
        category: 'workspace',
    },
    {
        key: 'workspace.search',
        label: 'Search workspace',
        description: 'Search resources available to the authorizing user.',
        category: 'workspace',
    },
    {
        key: 'workspace.update',
        label: 'Update workspace',
        description: 'Update settings for the authorized workspace.',
        category: 'workspace',
    },
    ...resourceDefinitions.flatMap(([resource, resourceLabel]) =>
        resourceActions.map(([action, actionLabel, description]) => ({
            key: `${resource}.${action}`,
            label: `${actionLabel} ${resourceLabel.toLowerCase()}`,
            description,
            category: 'resources' as const,
        })),
    ),
];

const scopePresentation: Record<string, ScopePresentation> = {
    user: {
        label: 'User Profile',
        description: 'Identity and profile information for the authorizing user.',
        icon: <TbUser />,
    },
    member: {
        label: 'Workspace Membership',
        description: "The authorizing user's membership in the active workspace.",
        icon: <TbUsersGroup />,
    },
    workspace: {
        label: 'Workspace',
        description: 'Workspace details, settings, and federated search.',
        icon: <TbBuilding />,
    },
    folder: {
        label: 'Folders',
        description: 'Workspace folders and their organization.',
        icon: <WorkspaceResourceGlyph resource='folders' />,
    },
    investment: {
        label: 'Investments',
        description: 'Investment research records and associated data.',
        icon: <WorkspaceResourceGlyph resource='investment' />,
    },
    memo: {
        label: 'Memos',
        description: 'Research memos and their content.',
        icon: <WorkspaceResourceGlyph resource='memo' />,
    },
    pipeline: {
        label: 'Pipelines',
        description: 'Pipeline records, stages, and workflows.',
        icon: <WorkspaceResourceGlyph resource='pipeline' />,
    },
    team: {
        label: 'Teams',
        description: 'Workspace teams and memberships.',
        icon: <WorkspaceResourceGlyph resource='team' />,
    },
    dashboard: {
        label: 'Dashboards',
        description: 'Dashboards and reports.',
        icon: <WorkspaceResourceGlyph resource='dashboard' />,
    },
    list: {
        label: 'Lists',
        description: 'Workspace lists and their records.',
        icon: <WorkspaceResourceGlyph resource='list' />,
    },
    report: {
        label: 'Reports',
        description: 'Reports and report components.',
        icon: <WorkspaceResourceGlyph resource='report' />,
    },
    task: {
        label: 'Tasks',
        description: 'Workspace tasks and assignments.',
        icon: <WorkspaceResourceGlyph resource='task' />,
    },
};

const actionIcons: Record<string, ReactNode> = {
    read: <TbSearch />,
    search: <TbSearch />,
    create: <TbFileText />,
    update: <TbSettings />,
    manage: <TbSettings />,
};

const humanize = (value: string) =>
    value
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[._-]+/g, ' ')
        .replace(/\b\w/g, (character) => character.toUpperCase());

export function splitOAuthScopes(scopes: string[]) {
    return {
        account: scopes.filter((scope) => scope.startsWith('user.') || scope.startsWith('member.')),
        workspace: scopes.filter((scope) => scope.startsWith('workspace.')),
        resources: scopes.filter(
            (scope) => !scope.startsWith('user.') && !scope.startsWith('member.') && !scope.startsWith('workspace.'),
        ),
    };
}

interface BuildOAuthScopeTreeOptions {
    colorBorder: string;
    colorBgContainer: string;
    colorTextTertiary: string;
    includeDescriptions?: boolean;
}

export function buildOAuthScopeTree(
    scopes: OAuthScopeDefinition[],
    { colorBorder, colorBgContainer, colorTextTertiary, includeDescriptions = true }: BuildOAuthScopeTreeOptions,
): TreeDataNode[] {
    const grouped = new Map<string, OAuthScopeDefinition[]>();

    scopes.forEach((scope) => {
        const domain = scope.key.split('.')[0];
        grouped.set(domain, [...(grouped.get(domain) ?? []), scope]);
    });

    return Array.from(grouped.entries()).map(([domain, definitions]) => {
        const presentation = scopePresentation[domain] ?? {
            label: humanize(domain),
            description: '',
            icon: <TbSettings />,
        };

        return {
            key: `group:${domain}`,
            title: (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.25rem 0' }}>
                    {['user', 'member', 'workspace'].includes(domain) ? (
                        <Avatar
                            shape='square'
                            size={24}
                            icon={presentation.icon}
                            style={{
                                color: colorTextTertiary,
                                border: `1px solid ${colorBorder}`,
                                backgroundColor: colorBgContainer,
                            }}
                        />
                    ) : (
                        <WorkspaceResourceAvatar resource={domain} size={24} />
                    )}
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 500, lineHeight: '24px' }}>{presentation.label}</div>
                        {includeDescriptions && presentation.description && (
                            <small style={{ display: 'block', color: colorTextTertiary, lineHeight: '18px' }}>
                                {presentation.description}
                            </small>
                        )}
                    </div>
                </div>
            ),
            children: definitions.map((definition) => {
                const action = definition.key.split('.').at(-1) ?? '';

                return {
                    key: definition.key,
                    title: (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.25rem 0' }}>
                            <span
                                style={{
                                    width: 24,
                                    height: 24,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flex: '0 0 24px',
                                    color: colorTextTertiary,
                                    lineHeight: 0,
                                }}
                            >
                                {actionIcons[action]}
                            </span>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ lineHeight: '24px' }}>{definition.label}</div>
                                {includeDescriptions && (
                                    <small style={{ display: 'block', color: colorTextTertiary, lineHeight: '18px' }}>
                                        {definition.description}
                                    </small>
                                )}
                            </div>
                        </div>
                    ),
                };
            }),
        };
    });
}

export function getOAuthScopeDefinitions(scopes: string[], catalog = defaultOAuthScopeCatalog) {
    const catalogByKey = new Map(catalog.map((definition) => [definition.key, definition]));

    return scopes.map(
        (scope): OAuthScopeDefinition =>
            catalogByKey.get(scope) ?? {
                key: scope,
                label: humanize(scope.split('.').at(-1) ?? scope),
                description: scope,
                category:
                    scope.startsWith('user.') || scope.startsWith('member.')
                        ? 'account'
                        : scope.startsWith('workspace.')
                          ? 'workspace'
                          : 'resources',
            },
    );
}
